/**
 * «Мои тендеры» — every lot that matched one of the user's saved searches.
 *
 * Deliberately NOT part of `catalog-client.ts`: the rows are the catalog's own
 * cards (same `CatalogRow` wire shape, same `rowToItem` mapper — reused, not
 * re-derived, so a new card field never has to be added twice) but the QUERY is
 * a per-user ledger read, not a public browse. Keeping it separate is what
 * stops someone reaching for `fetchCatalog` here and losing the two fields the
 * page exists to show.
 */
import { apiRaw } from './api';
import { rowToItem, type CatalogItem, type CatalogRow } from './catalog-client';

/** A catalog card plus WHY it is on this page. */
export interface MyTenderItem extends CatalogItem {
  /** Every active saved search of this user that matched the lot — all of
   *  them, even while the list is filtered to one chip. */
  matchedSearchIds: string[];
  /** ISO timestamp of the newest match. What the «новое» dot compares against
   *  — never the tender's own `publishedAt`, because a lot published a week
   *  ago can enter a subscription today when its ИКПУ is backfilled. */
  matchedAt: string;
  /** Every match of this lot came from the create-time / edit-time backfill:
   *  it was already open before the subscription existed. Rendered like any
   *  other row, but never marked «новое» — `matchedAt` is the moment of the
   *  save, shared by up to 500 rows, not the moment anything was published. */
  backfilled: boolean;
}

export interface MyTendersResult {
  items: MyTenderItem[];
  total: number;
  offset: number;
  limit: number;
}

export interface MyTendersQuery {
  /** Chip filter: one of the user's own saved searches, or all of them. */
  searchId?: string;
  /** Show lots that are no longer biddable. Default off. */
  includeClosed?: boolean;
  /** 1-based. Page size is fixed server-side. */
  page?: number;
  lang?: string;
}

interface MyTendersRow extends CatalogRow {
  matchedSearchIds?: string[];
  matchedAt?: string;
  backfilled?: boolean;
}

interface MyTendersEnvelope {
  data: MyTendersRow[];
  meta?: { pagination?: { total: number; offset: number; limit: number } };
}

/** GET /me/saved-searches/matches — one page of the user's matched tenders. */
export async function fetchMyTenders(query: MyTendersQuery = {}): Promise<MyTendersResult> {
  const params = new URLSearchParams();
  if (query.searchId) params.set('searchId', query.searchId);
  // Only sent when ON. The server's default is "biddable only", so an explicit
  // `includeClosed=false` would be noise in every cache key and every log line.
  if (query.includeClosed) params.set('includeClosed', 'true');
  if (query.page && query.page > 1) params.set('page', String(query.page));
  if (query.lang) params.set('lang', query.lang);
  const qs = params.toString();

  const envelope = await apiRaw<MyTendersEnvelope>(
    `/me/saved-searches/matches${qs ? `?${qs}` : ''}`,
  );
  return {
    items: (envelope.data ?? []).map((row) => ({
      ...rowToItem(row),
      matchedSearchIds: row.matchedSearchIds ?? [],
      // `?? ''` rather than a fabricated date: an absent timestamp must make
      // the «новое» comparison FALSE, not mark every row as new.
      matchedAt: row.matchedAt ?? '',
      // `?? false` is the safe default in the SAME direction as `matchedAt ?? ''`
      // above: an older API that does not send the flag yields "not backfilled",
      // which reverts to the pre-2026-09-26 «новое» behaviour rather than
      // silently suppressing the marker on every row.
      backfilled: row.backfilled ?? false,
    })),
    total: envelope.meta?.pagination?.total ?? 0,
    offset: envelope.meta?.pagination?.offset ?? 0,
    limit: envelope.meta?.pagination?.limit ?? 30,
  };
}

/**
 * How many of this reader's matched lots are newer than `since` — the number on
 * the sidebar badge.
 *
 * Server-capped at 100 (`SavedSearchMatchesService.NEW_COUNT_CAP`): the badge
 * renders anything above 99 as `99+`, so a returned 100 means "at least 100",
 * never "exactly 100". Do not reuse it in a sentence that implies precision.
 */
export async function fetchNewMatchCount(since: Date): Promise<number> {
  const qs = new URLSearchParams({ since: since.toISOString() });
  const envelope = await apiRaw<{ data?: { newCount?: number } }>(
    `/me/saved-searches/matches/count?${qs.toString()}`,
  );
  return envelope.data?.newCount ?? 0;
}

/**
 * localStorage key for "when did this user last look at this page".
 *
 * Per USER, not per browser: a shared laptop would otherwise mark one person's
 * matches as already-seen for the other. Deliberately client-side — the server
 * has no field for it and adding one buys nothing: the marker is a reading aid,
 * not a fact anyone else needs, and losing it on a new device costs one page of
 * missing dots.
 */
export function lastVisitKey(userId: string): string {
  return `my-tenders:seen:${userId}`;
}

/** When this user last opened «Мои тендеры», or `null` if never / unreadable. */
export function readLastVisit(userId: string): number | null {
  try {
    const raw = localStorage.getItem(lastVisitKey(userId));
    if (!raw) return null;
    const ms = Number(raw);
    return Number.isFinite(ms) ? ms : null;
  } catch {
    // Private mode, or storage disabled. No marker beats a crash.
    return null;
  }
}

/** Stamp this visit. Failure is silent for the same reason as above. */
export function writeLastVisit(userId: string, at = Date.now()): void {
  try {
    localStorage.setItem(lastVisitKey(userId), String(at));
  } catch {
    /* no storage — the dots simply never settle */
  }
}
